import router from '@/router';
import { App as CapacitorApp, type URLOpenListenerEvent } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';

export function useDeepLink() {
  let appUrlListener: PluginListenerHandle | null = null;

  /**
   * Extract the path from a deep link URL
   * Handles multiple domains: app.gastronomia.ai, dev-gastronomia.backtik.ch, staging-gastronomia.backtik.ch
   */
  const extractPathFromUrl = (url: string): string | null => {
    try {
      const urlObj = new URL(url);

      // List of valid domains
      const validDomains = ['app.gastronomia.ai', 'dev-gastronomia.backtik.ch', 'staging-gastronomia.backtik.ch', 'gastronomia.backtik.ch'];

      // Check if the hostname matches any of our valid domains
      if (validDomains.includes(urlObj.hostname)) {
        // Return the pathname (e.g., /tabs/tabs2)
        return urlObj.pathname;
      }

      console.warn('[DeepLink] URL hostname not recognized:', urlObj.hostname);
      return null;
    } catch (error) {
      console.error('[DeepLink] Failed to parse URL:', url, error);
      return null;
    }
  };

  /**
   * Navigate to the extracted path
   */
  const navigateToPath = async (path: string) => {
    if (!path || path === '/') {
      console.log('[DeepLink] Root path, skipping navigation');
      return;
    }

    try {
      console.log('[DeepLink] Navigating to:', path);
      await router.push(path);
    } catch (error) {
      console.error('[DeepLink] Navigation error:', error);
    }
  };

  /**
   * Handle incoming deep link URL
   */
  const handleDeepLink = async (url: string) => {
    console.log('[DeepLink] Handling deep link:', url);

    const path = extractPathFromUrl(url);

    if (path) {
      await navigateToPath(path);
    }
  };

  /**
   * Check for initial deep link when app launches
   */
  const checkInitialDeepLink = async () => {
    try {
      const initialUrl = await CapacitorApp.getLaunchUrl();

      if (initialUrl?.url) {
        console.log('[DeepLink] App launched with URL:', initialUrl.url);
        await handleDeepLink(initialUrl.url);
      }
    } catch (error) {
      console.error('[DeepLink] Failed to check initial deep link:', error);
    }
  };

  /**
   * Initialize deep link handling
   */
  const initDeepLink = async () => {
    console.log('[DeepLink] Initializing deep link handling');

    // Check for initial deep link on app launch
    await checkInitialDeepLink();

    // Add listener for subsequent deep links (when app is in background)
    appUrlListener = await CapacitorApp.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      console.log('[DeepLink] App opened with URL:', event.url);
      await handleDeepLink(event.url);
    });
  };

  /**
   * Clean up listeners
   */
  const cleanup = () => {
    if (appUrlListener) {
      appUrlListener.remove();
      appUrlListener = null;
      console.log('[DeepLink] Listener removed');
    }
  };

  return {
    initDeepLink,
    cleanup,
    handleDeepLink,
  };
}
